import React, { FormEvent, useState } from 'react';

import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next/pages';

import { Button } from 'antd';

import { Container } from '@//components/common';
import { getFishSaleFilter } from '@/api/fish-sale';
import { getCutTypes } from '@/api/reference-data';
import { KATALOG_URL } from '@/constants/routes';
import { useApiTranslate } from '@/hooks/use-api-translate';
import useAsyncEffect from '@/hooks/use-async-effect';
import useMobile from '@/hooks/use-mobile';
import { TranslationNamespace } from '@/i18n';
import LabeledInput from '@/UI/LabeledInput';
import LabeledSelect from '@/UI/LabeledSelect';

import bgDesktop from './assets/bg-desktop.webp';
import bgMobile from './assets/bg-mobile.webp';

import cls from './FishSearch.module.scss';

type FishSearchFormData = {
	/** Местоположение */
	location?: string;
	/** Вид ВБР */
	kind_fish?: string;
	/** Тип разделки */
	type_cutting?: string;
	/** Изготовитель */
	product_manufacturer?: string;
	/** Масса нетто, кг */
	volume?: number;
};

type TextAndValue = {
	key?: string;
	text: string;
	value: string;
};

export default function FishSearch() {
	const { isMobile } = useMobile();
	const router = useRouter();
	const [data, setData] = useState<FishSearchFormData>({});
	const [fishProducts, setFishProducts] = useState<TextAndValue[]>([]);
	const [cutTypes, setCutTypes] = useState<TextAndValue[]>([]);
	const t = useTranslation(TranslationNamespace.Marketing).t;
	const tApi = useApiTranslate();

	// Получает список видов ВБР, задаёт options'ы для формы
	useAsyncEffect(async () => {
		try {
			const fishProducts = await getFishSaleFilter();
			setFishProducts(
				fishProducts?.kind_fish?.map((fishProduct) => ({
					key: String(fishProduct.id),
					value: String(fishProduct.id),
					text: tApi(fishProduct.title, fishProduct.title_en),
				})),
			);
		} catch (e) {
			console.error(e);
		}
	}, [t]);

	// Получает список типов разделки, задаёт options'ы для формы
	useAsyncEffect(async () => {
		const cutTypes = await getCutTypes();

		setCutTypes(
			cutTypes?.map((cutType) => ({
				key: cutType.title,
				value: String(cutType.id),
				text: tApi(cutType.title, cutType.title_en),
			})),
		);
	}, [t]);

	/**
	 * Обрабатывает отправку формы
	 * Редиректит на страницу "buy-fish" с query-параметрами из формы
	 **/
	const submitHandler = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		// TODO: сделать так, чтобы на странице buy-fish обрабатывались все переданные параметры
		await router.push({
			pathname: 'katalog',
			query: data,
		});
	};

	return (
		<section
			style={{ backgroundImage: `url(${isMobile ? bgMobile.src : bgDesktop.src})` }}
			className={cls.section}
		>
			<Container className={cls.container}>
				<div className={cls.info}>
					<h2 className={cls.title}>
						{t('main-landing.fish-search.purchase-fish-products-at-a-favorable-price')}
					</h2>
					<p className={cls.subtitle}>
						{t(
							'main-landing.fish-search.we-show-you-at-which-warehouse-the-fish-you-need-is-stored-and-who-sells',
						)}
					</p>
					<Button size="large" className={cls.moreButton} href={KATALOG_URL}>
						{t('main-landing.fish-search.find-out-more')}
					</Button>
				</div>

				<form onSubmit={submitHandler} className={cls.card}>
					<p className={cls.cardTitle}>{t('main-landing.fish-search.search-for-fish')}</p>
					<div className={cls.formFields}>
						<LabeledInput
							label={t('main-landing.fish-search.location')}
							value={data.location}
							inputtype="location"
							filterFromBound="city"
							filterToBound="city"
							placeholder={t('main-landing.fish-search.select-city-or-region')}
							onUpdate={(e) => setData({ ...data, location: e.data.city })}
							onInput={(e) => setData({ ...data, location: e.currentTarget.value })}
						/>
						<div className={cls.formRow}>
							<LabeledSelect
								label={t('main-landing.fish-search.fish-kind')}
								value={data.kind_fish}
								options={fishProducts}
								placeholder={t('main-landing.fish-search.select-type')}
								resetOption={t('main-landing.fish-search.not-selected')}
								onChange={(product) => setData({ ...data, kind_fish: product })}
							/>
							<LabeledSelect
								label={t('main-landing.fish-search.cut-type')}
								value={data.type_cutting}
								options={cutTypes}
								placeholder={t('main-landing.fish-search.select-type')}
								resetOption={t('main-landing.fish-search.not-selected')}
								onChange={(cutType) => setData({ ...data, type_cutting: cutType })}
							/>
						</div>
						<div className={cls.formRow}>
							<LabeledInput
								label={t('main-landing.fish-search.manufacturer')}
								value={data.product_manufacturer}
								placeholder={t('main-landing.fish-search.select-manufacturer')}
								onChange={(e) =>
									setData({
										...data,
										product_manufacturer: e.currentTarget.value,
									})
								}
							/>
							<LabeledInput
								label={t('main-landing.fish-search.net-weight-kg')}
								value={data.volume}
								inputtype="number"
								placeholder={t('main-landing.fish-search.enter-mass')}
								onChange={(e) => setData({ ...data, volume: +e.currentTarget.value })}
							/>
						</div>
					</div>
					<Button htmlType="submit" type="primary" size="large" className={cls.submitButton}>
						{t('main-landing.fish-search.search-for-fish-2')}
					</Button>
				</form>
			</Container>
		</section>
	);
}
